import { Injectable } from '@angular/core';
import { State, Action, StateContext, Store, Selector } from '@ngxs/store';
import { TrovataAppState } from 'src/app/core/models/state.model';
import { checkToAddStateApiError, checkToRemoveStateApiError, StateApiError } from 'src/app/core/models/state-error.model';
import { combineLatest, firstValueFrom, Observable, filter, take } from 'rxjs';
import { InstitutionCountry, InstitutionAddtlInstruction } from '../../../models/payment.model';
import { PaymentsHttpService } from '../../../services/payments/payments-http.service';
import { CustomerFeatureState } from 'src/app/features/settings/store/state/customer-feature.state';
import { PermissionId, PermissionMap } from 'src/app/features/settings/models/feature.model';
import { FlagIconSize, getFlagIconURLByCode } from '@trovata/app/shared/utils/country-flag-svg-helper';
import {
	ClearInstructionsState,
	InitInstructionsState,
	ResetInstructionsState,
	GetInstitutionInstructions,
	GetInstitutionCountries,
} from '../../actions/instructions.actions';

export class InstructionsStateModel {
	institutionInstructionsMap: InstitutionLevelInstructions;
	apiErrors: StateApiError[];
	isCached: boolean;
}

export interface CountryLevelInstructions {
	[countryCode: string]: InstitutionAddtlInstruction[];
}

export interface PaymentTypeLevelInstructions {
	instructions: CountryLevelInstructions;
	availableCountries: InstitutionCountry[];
}

export interface InstitutionLevelInstructions {
	[institutionId: string]: { [paymentType: string]: PaymentTypeLevelInstructions };
}

@State<InstructionsStateModel>({
	name: 'instructions',
	defaults: {
		institutionInstructionsMap: {},
		isCached: false,
		apiErrors: [],
	},
})
@Injectable()
export class InstructionsState {
	private userAvailablePermissions$: Observable<PermissionMap>;
	private paymentsEnabled$: Observable<boolean>;

	private appReady$: Observable<boolean>;

	constructor(
		private paymentsHttpService: PaymentsHttpService,
		private store: Store
	) {
		this.appReady$ = this.store.select((state: TrovataAppState) => state.core.appReady);
		this.userAvailablePermissions$ = this.store.select(CustomerFeatureState.permissionIds);
		this.paymentsEnabled$ = this.store.select(CustomerFeatureState.paymentsEnabled);
	}

	@Selector()
	static institutionInstructionsMap(state: InstructionsStateModel): InstitutionLevelInstructions {
		return state.institutionInstructionsMap;
	}

	@Action(InitInstructionsState)
	async initInstructionsState(context: StateContext<InstructionsStateModel>): Promise<void> {
		const [appReady, permissions, paymentsEnabled]: [boolean, PermissionMap, boolean] = await firstValueFrom(
			combineLatest([this.appReady$, this.userAvailablePermissions$, this.paymentsEnabled$]).pipe(
				filter(([ready, perms, enabled]: [boolean, PermissionMap, boolean]) => !!ready && !!perms && enabled !== undefined),
				take(1)
			)
		);

		if (!paymentsEnabled) {
			return;
		}

		if (permissions.has(PermissionId.readPayments)) {
			const defaults: Partial<InstructionsStateModel> = { institutionInstructionsMap: {}, apiErrors: [] };
			context.patchState(defaults);
		} else {
			const cleared: Partial<InstructionsStateModel> = { institutionInstructionsMap: {}, apiErrors: [] };
			context.patchState(cleared);
		}
	}

	@Action(GetInstitutionInstructions)
	getInstitutionInstructions(context: StateContext<InstructionsStateModel>, action: GetInstitutionInstructions): Promise<InstitutionAddtlInstruction[]> {
		return new Promise<InstitutionAddtlInstruction[]>(async (resolve, reject) => {
			try {
				const instructions: InstitutionAddtlInstruction[] = await firstValueFrom(
					this.paymentsHttpService.getInstitutionInstructions(action.countryCode, action.institution, action.paymentType)
				);
				checkToRemoveStateApiError(context, GetInstitutionInstructions);
				instructions.forEach((instruction: InstitutionAddtlInstruction) => {
					instruction.fieldOptionsDisplay = instruction.fieldOptions?.map((opt: Object) => {
						const key: string = Object.keys(opt)?.[0];
						return { value: key, display: opt[key] };
					});
				});
				instructions.sort((aInstr: InstitutionAddtlInstruction, bInstr: InstitutionAddtlInstruction) => bInstr.fieldOrder - aInstr.fieldOrder);
				const state: InstructionsStateModel = context.getState();
				if (!state.institutionInstructionsMap[action.institution]) {
					state.institutionInstructionsMap[action.institution] = {};
				}
				if (!state.institutionInstructionsMap[action.institution][action.paymentType]) {
					state.institutionInstructionsMap[action.institution][action.paymentType] = { instructions: {}, availableCountries: null };
				}
				state.institutionInstructionsMap[action.institution][action.paymentType].instructions[action.countryCode] = instructions;
				await context.patchState(state);
				resolve(instructions);
			} catch (err) {
				checkToAddStateApiError(err, context, GetInstitutionInstructions, 'Institution additional instructions are down right now. Please try again later.');
				reject(err);
			}
		});
	}

	@Action(GetInstitutionCountries)
	getInstitutionCountries(context: StateContext<InstructionsStateModel>, action: GetInstitutionCountries): Promise<InstitutionCountry[]> {
		return new Promise<InstitutionCountry[]>(async (resolve, reject) => {
			try {
				const countries: InstitutionCountry[] = await firstValueFrom(this.paymentsHttpService.getInstitutionCountries(action.institution, action.paymentType));
				checkToRemoveStateApiError(context, GetInstitutionInstructions);
				countries.map((country: InstitutionCountry) => {
					country.flagUrl = getFlagIconURLByCode(country.countryCode, FlagIconSize.fourByThree);
					return country;
				});
				const state: InstructionsStateModel = context.getState();
				if (!state.institutionInstructionsMap[action.institution]) {
					state.institutionInstructionsMap[action.institution] = {};
				}
				if (!state.institutionInstructionsMap[action.institution][action.paymentType]) {
					state.institutionInstructionsMap[action.institution][action.paymentType] = { instructions: {}, availableCountries: null };
				}
				state.institutionInstructionsMap[action.institution][action.paymentType].availableCountries = countries;
				await context.patchState(state);
				resolve(countries);
			} catch (err) {
				checkToAddStateApiError(err, context, GetInstitutionCountries, 'Institution available countries are down right now. Please try again later.');
				reject(err);
			}
		});
	}

	@Action(ClearInstructionsState)
	clearInstructionsState(context: StateContext<InstructionsStateModel>): void {
		const state: InstructionsStateModel = context.getState();
		Object.keys(state).forEach((key: string) => {
			state[key] = null;
		});
		context.patchState(state);
	}

	@Action(ResetInstructionsState)
	resetInstructionsState(context: StateContext<InstructionsStateModel>): void {
		context.dispatch(new ClearInstructionsState());
	}
}
